/**
 * Storage helpers shared by useOnboardingStore and the boot-time narration
 * check, which decides synchronously — before hydration or any fetch —
 * whether the overlay must cover the boot.
 *
 * The viewer isn't known that early, but the first URL segment IS the graph's
 * owner, and only that account's onboarding may narrate. Scanning every stored
 * entry instead handed the overlay to anyone, anonymous visitors included.
 */
import { VIEW_TYPES, deriveViewTypeFromLocation } from '../utils/viewType'
import { isGraphEmptyOnLoad } from '../components/FirstEntry/legacyBridge'

const STATE_KEY_PREFIX = 'onboardingGuide.v1:'

export const storageKeyFor = username => `${STATE_KEY_PREFIX}${username}`

// Persisted so the line progression survives the reload between import submit
// and graph ready; a module-level clock would restart.
const NARRATION_STARTED_AT_KEY = 'onboardingNarrationStartedAt'

export function readNarrationStartedAt() {
    try {
        const raw = window.sessionStorage.getItem(NARRATION_STARTED_AT_KEY)
        const timestamp = Number(raw)
        return raw && Number.isFinite(timestamp) ? timestamp : null
    } catch {
        return null
    }
}

export function writeNarrationStartedAt(timestamp) {
    try {
        window.sessionStorage.setItem(
            NARRATION_STARTED_AT_KEY,
            String(timestamp)
        )
    } catch {}
}

export function clearNarrationStartedAt() {
    try {
        window.sessionStorage.removeItem(NARRATION_STARTED_AT_KEY)
    } catch {}
}

// First path segments that can never be the owner of a /:user/:graph page.
const NON_GRAPH_ROOTS = [
    'apps',
    'import',
    'imports',
    'subscription',
    'upgrade',
    'api-access',
    'settings',
    'admin-panel',
    'evernote',
    'login',
    'signup',
    'thank-you',
    'about',
    'tutorial',
    'extension',
    'mcp',
    'user',
]

// Third segments of /:user/:graph/:view that are still graph views — each
// importer redirects to its own. Duplicated in
// views/components/entries/onboardingBootNarration.ejs; keep in sync.
const GRAPH_VIEW_TAILS = [
    'edit',
    'rss',
    'seo',
    'niche',
    'mindmap',
    'google',
    'kwrds',
    'twitter',
]

// Both URL shapes of a graph context page count: /:user/:graph and the
// /:user/:graph/:view forms the importers redirect to.
export function isGraphContextPath(pathname) {
    const segments = (pathname || '').split('/').filter(Boolean)
    if (NON_GRAPH_ROOTS.includes(segments[0])) return false
    const hasGraphUrlShape =
        segments.length === 2 ||
        (segments.length === 3 && GRAPH_VIEW_TAILS.includes(segments[2]))
    if (!hasGraphUrlShape) return false
    // `/:user/edit` (Top Synthesis) and `/:user/find` (Search) share the
    // two-segment shape, so shape alone can't tell them from a graph.
    return (
        deriveViewTypeFromLocation({ pathname, search: '' }) ===
        VIEW_TYPES.CONTEXT
    )
}

/**
 * The username whose narration should cover this page load, or null: only the
 * graph's owner, and only while they are mid-onboarding at the bring stage.
 * If the resolved session later contradicts this guess, the guide's
 * cancellation guards tear the overlay down.
 */
export function getBootNarrationUser() {
    if (typeof window === 'undefined') return null
    try {
        const { pathname } = window.location
        if (!isGraphContextPath(pathname)) return null
        const graphOwner = pathname.split('/').filter(Boolean)[0]
        if (!graphOwner) return null
        const raw = window.localStorage.getItem(storageKeyFor(graphOwner))
        const state = raw ? JSON.parse(raw) : {}
        if (state.status !== 'active' || (state.stage || 'bring') !== 'bring') {
            return null
        }
        // A graph just made empty has nothing to narrate. A running clock means
        // an import was submitted, which narrates even if this page rendered
        // before the import's data was saved.
        if (isGraphEmptyOnLoad() && readNarrationStartedAt() === null) {
            return null
        }
        return graphOwner
    } catch {
        return null
    }
}
